import React, { useState } from "react";
import Modal from "@/components/chat/Modal";
import { Plus, X, BarChart3, Users } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { getInitials, avatarColor } from "@/lib/avatar";

export function PollView({ m }) {
  const { user } = useAuth();
  const poll = m.poll || {};
  const votes = m.votes || {};
  const myVotes = votes[user.id] || [];
  const [voters, setVoters] = useState(null);
  const counts = {};
  let total = 0;
  Object.values(votes).forEach((arr) => (arr || []).forEach((o) => { counts[o] = (counts[o] || 0) + 1; total++; }));

  const vote = async (oid) => {
    try { await api.post(`/messages/${m.id}/vote`, { option_id: oid }); }
    catch { toast.error("Could not vote"); }
  };
  const openVoters = async (e) => {
    e.stopPropagation();
    try { const { data } = await api.get(`/messages/${m.id}/voters`); setVoters(data); }
    catch { toast.error("Could not load votes"); }
  };
  const optText = (id) => (poll.options || []).find((o) => o.id === id)?.text || "";

  return (
    <div className="min-w-[240px]" data-testid={`poll-${m.id}`}>
      <div className="flex items-center gap-2 mb-2 text-[var(--rc-gold)]"><BarChart3 size={16} /><span className="text-[11px] font-medium uppercase tracking-wide">Poll</span></div>
      <p className="font-medium mb-3 text-[15px] break-words">{poll.question}</p>
      <div className="space-y-2">
        {(poll.options || []).map((o) => {
          const c = counts[o.id] || 0;
          const pct = total ? Math.round((c / total) * 100) : 0;
          const mine = myVotes.includes(o.id);
          return (
            <button key={o.id} onClick={(e) => { e.stopPropagation(); vote(o.id); }} data-testid={`poll-opt-${m.id}-${o.id}`}
              className={`relative w-full text-left rounded-xl border overflow-hidden px-3 py-2 ${mine ? "border-[var(--rc-gold)]" : "border-[var(--rc-border)]"}`}>
              <span className="absolute inset-0 bg-[var(--rc-gold)]/15 transition-all" style={{ width: `${pct}%` }} />
              <span className="relative flex items-center justify-between text-sm">
                <span className="truncate pr-2">{mine ? "● " : ""}{o.text}</span>
                <span className="text-[var(--rc-muted)] tabular-nums shrink-0">{pct}%</span>
              </span>
            </button>
          );
        })}
      </div>
      <div className="flex items-center justify-between mt-2">
        <p className="text-[11px] text-[var(--rc-muted)]">{total} vote{total === 1 ? "" : "s"}{poll.multi ? " · multiple choice" : ""}</p>
        {total > 0 && <button onClick={openVoters} data-testid={`poll-voters-btn-${m.id}`} className="flex items-center gap-1 text-[11px] text-[var(--rc-gold)]"><Users size={12} /> View votes</button>}
      </div>

      {voters !== null && (
        <Modal title="Poll results" onClose={() => setVoters(null)}>
          <div className="space-y-4" data-testid="poll-voters-dialog" onClick={(e) => e.stopPropagation()}>
            <p className="font-medium">{poll.question}</p>
            {(poll.options || []).map((o) => {
              const list = voters[o.id] || [];
              return (
                <div key={o.id}>
                  <div className="flex items-center justify-between text-sm mb-2"><span>{o.text}</span><span className="text-[var(--rc-muted)]">{list.length}</span></div>
                  <div className="space-y-2 pl-1">
                    {list.length === 0 && <p className="text-xs text-[var(--rc-muted)]">No votes yet</p>}
                    {list.map((p) => (
                      <div key={p.id} className="flex items-center gap-2" data-testid={`voter-${o.id}-${p.id}`}>
                        <div className="h-7 w-7 rounded-full grid place-items-center text-[var(--rc-gold)] text-[10px] font-semibold overflow-hidden border-2 border-[var(--rc-gold)]" style={{ backgroundColor: avatarColor(p.name) }}>
                          {p.avatar ? <img src={p.avatar} alt="" className="h-full w-full object-cover" /> : getInitials(p.name)}
                        </div>
                        <span className="text-sm">{p.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </Modal>
      )}
    </div>
  );
}

export function PollComposer({ onClose, onCreate }) {
  const [question, setQuestion] = useState("");
  const [options, setOptions] = useState(["", ""]);
  const [multi, setMulti] = useState(false);
  const setOpt = (i, v) => setOptions((o) => o.map((x, idx) => (idx === i ? v : x)));
  const valid = question.trim() && options.filter((o) => o.trim()).length >= 2;

  return (
    <Modal title="New poll" onClose={onClose}>
      <div className="space-y-3" data-testid="poll-composer">
        <input value={question} onChange={(e) => setQuestion(e.target.value)} placeholder="Ask a question…" data-testid="poll-question"
          className="w-full bg-[var(--rc-bg)] border border-[var(--rc-border)] rounded-xl px-4 py-3 focus:outline-none focus:border-[var(--rc-gold)]/50" />
        {options.map((o, i) => (
          <div key={i} className="flex items-center gap-2">
            <input value={o} onChange={(e) => setOpt(i, e.target.value)} placeholder={`Option ${i + 1}`} data-testid={`poll-option-${i}`}
              className="flex-1 bg-[var(--rc-bg)] border border-[var(--rc-border)] rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-[var(--rc-gold)]/50" />
            {options.length > 2 && <button onClick={() => setOptions((os) => os.filter((_, idx) => idx !== i))} className="text-[var(--rc-muted)]"><X size={16} /></button>}
          </div>
        ))}
        {options.length < 10 && <button onClick={() => setOptions((o) => [...o, ""])} data-testid="poll-add-option" className="flex items-center gap-2 text-sm text-[var(--rc-gold)]"><Plus size={16} /> Add option</button>}
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={multi} onChange={(e) => setMulti(e.target.checked)} data-testid="poll-multi" /> Allow multiple answers</label>
        <button disabled={!valid} onClick={() => onCreate({ question: question.trim(), options: options.map((o) => o.trim()).filter(Boolean), multi })} data-testid="poll-create-btn"
          className="w-full rounded-xl bg-[var(--rc-gold)] text-black font-semibold py-3 disabled:opacity-50">Create poll</button>
      </div>
    </Modal>
  );
}
